import * as Sentry from '@sentry/browser'
import clsx from 'clsx'
import { computed, defineComponent } from 'vue'

import VButton from '@/components/common/button/VButton'
import VTextField from '@/components/common/text-field/VTextField'
import VDropdown from '@/components/common/VDropdown'
import VIcon from '@/components/common/VIcon'
import VPagination from '@/components/common/VPagination'
import BookingListList from '@/components/views/booking-list/BookingListList'
import BookingListTable from '@/components/views/booking-list/BookingListTable'
import EmptyBookingList from '@/components/views/booking-list/EmptyBookingList'
import useAuth from '@/composables/auth/useAuth'
import useBreakpointsValue from '@/composables/common/useBreakpointsValue'
import useLocalization from '@/composables/localization/useLocalization'
import useBookingList, { STATUS_ITEMS } from '@/composables/views/booking-list/useBookingListView'
import { getLocalizationFirstName } from '@/utils/localization'
import { stringToCurrencyFormat } from '@/utils/number'
import { isNil } from '@/utils/validation'

export default defineComponent(() => {
  const { localeTexts, translationWithFragmentKeys } = useLocalization()
  const auth = useAuth()
  const bookingListContent = useBreakpointsValue(BookingListList, { desktop: BookingListTable })
  const { page, bookingListQuery, statusSelected, handleChangePage } = useBookingList()

  bookingListQuery.execute()

  Sentry.setUser({ email: auth.userProfile?.email })

  // #region Getters

  const userImageProfile = computed((): JSX.Element => {
    if (auth.userProfile?.image_url !== null) {
      return (
        <img
          t-data='booking-list-user-image'
          class='size-18 rounded'
          src={auth.userProfile?.image_url}
        />
      )
    }

    return (
      <div class='relative rounded border border-dashed border-neutral-300 bg-neutral-200 p-[21px]'>
        <VIcon
          t-data='booking-list-user-image-default'
          class='text-[28px] text-neutral-300'
          icon='material-symbols:account-box-outline'
        />
      </div>
    )
  })

  const totalPoints = computed((): string | undefined => {
    if (isNil(auth.userProfile?.total_points)) {
      return undefined
    }

    return stringToCurrencyFormat(auth.userProfile.total_points)
  })

  const agentName = computed((): string => {
    if (!auth.userProfile) {
      return ''
    }

    return translationWithFragmentKeys(
      'agent-main-heading',
      [
        { fragment_key: 'agent-firstname-en', value: getLocalizationFirstName(auth.userProfile, auth.userProfile.language_preference) }
      ])
  })

  const organizationName = computed((): string => {
    return translationWithFragmentKeys('agent-main-paragraph', [{ fragment_key: 'organization-name', value: auth.userProfile?.organization?.name }])
  })

  const content = computed((): JSX.Element | undefined => {
    if (bookingListQuery.data.value?.data === undefined) {
      return undefined
    }

    if (bookingListQuery.data.value.total === 0) {
      return <EmptyBookingList />
    }

    return (
      <div class='flex h-auto w-full flex-col'>
        <div class='hidden pt-14 tablet:flex'>
          <div class='flex-1'>
            <div class='flex flex-1 space-x-4'>
              {userImageProfile.value}
              <div class='flex flex-col space-y-2 py-1'>
                <div class='flex font-roboto text-heading-5'>
                  <span t-data='booking-list-agent-name'>
                    {agentName.value}
                  </span>
                </div>
                <div class='flex'>
                  <span
                    t-data='booking-list-organization-name'
                    class='text-body-1 font-medium text-neutral'
                  >
                    {organizationName.value}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div class='flex-none'>
            <div class='flex flex-col items-end justify-center'>
              <div class='flex space-x-1'>
                <VIcon
                  class='mt-1 text-[24px] text-warning-400'
                  icon='material-symbols:star'
                />
                <div
                  t-data='agent-total-points'
                  class='flex font-roboto text-heading-5 text-warning'
                >
                  {totalPoints.value}
                </div>
              </div>
              <div class='flex text-caption text-neutral-500'>
                {localeTexts.value['agent-main-point-label']}
              </div>
            </div>
          </div>
        </div>
        <div class='mt-4 hidden items-center justify-end tablet:flex'>
          <div class='hidden w-full flex-1 space-x-6'>
            <VTextField
              class='w-[184px]'
              type='text'
              placeholder='Search booking'
              value=''
            >
              {{
                append () {
                  return (
                    <VIcon
                      class='text-[24px] text-neutral-800'
                      icon='material-symbols:search'
                    />
                  )
                }
              }}
            </VTextField>
            <VDropdown
              class='w-[184px]'
              value={statusSelected.value}
              placeholder='Status'
              items={STATUS_ITEMS}
              highlightEnabled
              onInput={(value) => (statusSelected.value = value)}
            />
          </div>
          <div class='flex flex-none'>
            <VButton
              class='btn-medium'
              to='/booking'
            >
              {localeTexts.value['agent-main-booking-btn']}
            </VButton>
          </div>
        </div>
        <div class='flex flex-col items-center pt-8 tablet:hidden'>
          <div class='flex font-roboto text-heading-4'>
            <span t-data='booking-list-name'>
              {agentName.value}
            </span>
          </div>
          <div>
            <span
              t-data='booking-list-detail'
              class='text-body-2 text-neutral-500'
            >
              {organizationName.value}
            </span>
          </div>
          <div class='mb-4 mt-2.5 flex space-x-1'>
            <VIcon
              class='mt-1 text-[24px] text-warning-400'
              icon='material-symbols:star'
            />
            <div
              t-data='agent-mobile-total-points'
              class='flex font-roboto text-heading-6 text-warning'
            >
              {totalPoints.value}
            </div>
          </div>
          <VButton
            to='/booking'
          >
            {localeTexts.value['agent-main-booking-btn']}
          </VButton>
          <div class='mt-8 hidden w-full justify-between space-x-4 px-4'>
            <VTextField
              class='w-[295px]'
              type='text'
              placeholder='Search booking'
              value=''
            >
              {{
                append () {
                  return (
                    <VIcon
                      class='text-[24px] text-neutral-800'
                      icon='material-symbols:search'
                    />
                  )
                }
              }}
            </VTextField>
            <VButton
              class='btn-medium'
              color={clsx('btn-white')}
            >
              <VIcon
                class='text-[24px] text-neutral'
                icon='material-symbols:filter-alt'
              />
            </VButton>
          </div>
        </div>
        <div class='mt-6 flex px-4 tablet:mt-4 tablet:px-0'>
          <span class='text-body-2 text-neutral-500'>
            {translationWithFragmentKeys('agent-main-booking-list-item-label', [{ fragment_key: 'number', value: bookingListQuery.data.value.total }])}
          </span>
        </div>
        <div class='flex h-full flex-col overflow-y-auto overflow-x-hidden bg-white'>
          <bookingListContent.value />
          <div class='flex justify-center bg-white px-4 pb-6 pt-4 tablet:justify-end tablet:pb-[19px]'>
            <VPagination
              itemsPerPage={bookingListQuery.data.value.limit}
              totalItemsNumber={bookingListQuery.data.value.total}
              currentPage={page.value}
              optionalTotalPages={bookingListQuery.data.value.total_page}
              onCurrentPageChange={handleChangePage}
            />
          </div>
        </div>
      </div>
    )
  })

  // #endregion

  return () => (
    <div class='h-fit min-h-full bg-white px-0 tablet:px-11 desktop:px-16'>
      {content.value}
    </div>
  )
}, {
  name: 'BookingListView'
})
